// // /////////////////////////////////////////////////////////////////////////////////////
// //                              Copyright (c) 2021 - 2025
// //                                    RXO Inc.
// //                          All Rights Reserved Worldwide
// //
// // WARNING:  This program (or document) is unpublished, proprietary
// // property of RXO Inc. and is to be maintained in strict confidence.
// // Unauthorized reproduction, distribution or disclosure of this program
// // (or document), or any program (or document) derived from it is
// // prohibited by State and Federal law, and by local law outside of the U.S.
// // /////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tab, TabList } from "react-tabs";

import messages from "../../../helpers/messages";
import {
  analyticsGridView,
  analyticsGridViewV2,
  division as divisionType
} from "../../../helpers/types";
import {
  fetchDatRates,
  resetLoadFilters,
  setAnalyticsGridView
} from "../../../slices/analyticsSlice";
import IconButton from "../../form-controls/IconButton";
import ClearFilterIcon from "../../icons/ClearFilterIcon";
import { DATRateViewDrawer } from "./DATRateViewDrawer";
import { DATRateViewButton } from "./DATRateViewButton";
import LaneForecastInfo from "./LaneForecastInfo";

export const RadialBar = () => {
  const dispatch = useDispatch();
  const {
    analytics: {
      selectedAnalyticsGridView,
      laneView: { filtersApplied: laneViewFilters },
      loadView: { filtersApplied: loadViewFilters },
      shipperView: { filtersApplied: shipperViewFilters },
      carrierView: { filtersApplied: carrierViewFilters },
      shipperRatesView: { filtersApplied: shipperRatesFilters },
      loads,
      aggregatedForecastPricing,
      filters
    },
    featureFlags: { enableShipperRatesView },
    user: { user },
    bidSummary: { summary }
  } = useSelector(state => state);
  const [openDrawer, setOpenDrawer] = useState(false);

  const bidDivision = summary?.division;
  const division = bidDivision ?? user.division;
  let showViewDATRateViewButton = user.division === divisionType.coy;

  if (bidDivision && bidDivision !== divisionType.coy) {
    showViewDATRateViewButton = false;
  }

  return (
    <>
      <div
        data-testid="radial-bar"
        className={`radial-bar ${
          enableShipperRatesView ? "" : "pl-28"
        } flex items-center`}
      >
        <div className="t35">
          {enableShipperRatesView ? (
            <TabList>
              {analyticsGridViewV2.map(
                ({ value, label, division: tabDivision }) =>
                  (tabDivision === division || tabDivision === null) && (
                    <Tab
                      key={`analytics-${value}`}
                      selected={value === selectedAnalyticsGridView}
                      onClick={() => dispatch(setAnalyticsGridView(value))}
                    >
                      {label}
                    </Tab>
                  )
              )}
            </TabList>
          ) : (
            analyticsGridView.map(({ value, label }) => (
              <span key={value}>
                <input
                  radioGroup="analytics-grid-view"
                  type="radio"
                  id={`analytics-${value}`}
                  value={value}
                  className="mt-0 cursor-pointer"
                  name="analyticsGridView"
                  onChange={() => dispatch(setAnalyticsGridView(value))}
                  checked={value === selectedAnalyticsGridView}
                />
                <label
                  htmlFor={`analytics-${value}`}
                  className="font-size-12 ml-8"
                >
                  {label}
                </label>
              </span>
            ))
          )}
        </div>
        <div className="text-right t65">
          {aggregatedForecastPricing ? (
            <LaneForecastInfo
              aggregatedForecastPricing={aggregatedForecastPricing}
              filters={filters}
            />
          ) : null}
          {showViewDATRateViewButton ? (
            <DATRateViewButton
              onClick={() => {
                setOpenDrawer(true);
                dispatch(fetchDatRates());
              }}
              className="mr-12"
            />
          ) : null}
          <IconButton
            title={messages.clearFilters.defaultMessage}
            disabled={
              !(
                laneViewFilters ||
                loadViewFilters ||
                shipperViewFilters ||
                carrierViewFilters ||
                shipperRatesFilters ||
                loads?.some(l => l.exclude)
              )
            }
            dataTestId="clear-filter-button"
            icon={ClearFilterIcon}
            offset={[7, 6]}
            className="mr-40"
            onClick={() => dispatch(resetLoadFilters())}
          />
        </div>
      </div>
      <DATRateViewDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
      />
    </>
  );
};

export default RadialBar;
